import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { STATO_STYLE } from "@/lib/constants";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function pad(n) { return String(n).padStart(2, "0"); }

export default function TeamCalendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [onlyMine, setOnlyMine] = useState(true);
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => { api.get("/trips").then((r) => setTrips(r.data)); }, []);

  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const teamTrips = useMemo(() => {
    if (user.role === "amministratore") return trips;
    if (user.role === "responsabile" && onlyMine) {
      const mine = trips.filter((t) => (t.responsabile || "").toLowerCase() === (user.name || "").toLowerCase());
      return mine;
    }
    return trips;
  }, [trips, user, onlyMine]);

  const weeks = useMemo(() => {
    const first = new Date(year, month, 1);
    const startDow = (first.getDay() + 6) % 7; // Lunedì = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  }, [year, month]);

  const tripsForDay = (date) => {
    const ds = `${year}-${pad(month + 1)}-${pad(date.getDate())}`;
    return teamTrips.filter((t) => {
      const s = t.data_partenza, e = t.data_rientro || t.data_partenza;
      return s && ds >= s && ds <= (e || s);
    });
  };

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="text-[#E30613]" /> Calendario Team
          </h1>
          <p className="text-slate-500 mt-1">Tutte le trasferte del gruppo nel mese.</p>
        </div>
        {user.role === "responsabile" && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="only-mine-toggle">
            <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} className="accent-[#E30613]" />
            Solo il mio gruppo
          </label>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6" data-testid="team-calendar">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="tc-prev" onClick={() => setMonthDate(new Date(year, month - 1, 1))} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ChevronLeft size={18} /></button>
          <div className="font-display text-xl font-semibold">{MESI[month]} {year}</div>
          <button data-testid="tc-next" onClick={() => setMonthDate(new Date(year, month + 1, 1))} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ChevronRight size={18} /></button>
        </div>

        <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-lg overflow-hidden border border-slate-100">
          {GIORNI.map((g) => (
            <div key={g} className="bg-slate-50 text-center py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">{g}</div>
          ))}
          {weeks.flat().map((date, i) => {
            if (!date) return <div key={i} className="bg-white min-h-[92px]" />;
            const dayTrips = tripsForDay(date);
            const ds = `${year}-${pad(month + 1)}-${pad(date.getDate())}`;
            const isToday = ds === todayStr;
            return (
              <div key={date.toISOString()} className="bg-white min-h-[92px] p-1.5" data-testid={`tc-day-${date.toISOString().slice(0, 10)}`}>
                <div className={`text-xs font-medium mb-1 h-5 w-5 flex items-center justify-center rounded-full ${isToday ? "bg-[#E30613] text-white" : "text-slate-400"}`}>{date.getDate()}</div>
                <div className="space-y-1">
                  {dayTrips.slice(0, 3).map((t) => (
                    <button
                      key={t.id}
                      onClick={() => navigate(`/trasferte/${t.id}`)}
                      data-testid={`tc-trip-${t.id}`}
                      className={`w-full text-left text-[10px] leading-tight truncate rounded px-1.5 py-0.5 border ${STATO_STYLE[t.stato] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                      title={`${t.destinazione} · ${t.dipendente}`}
                    >
                      {t.destinazione} · {t.dipendente?.split(" ")[0]}
                    </button>
                  ))}
                  {dayTrips.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{dayTrips.length - 3} altre</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
